// Server-rendered avatar: the GPU renderer produces the frames, this file just
// feeds it audio and paints what comes back.
//
//   agent audio (LiveKit) ─┬─► 16 kHz PCM ─► gateway ─► renderer ─► JPEGs ─► canvas
//                          └─► DelayNode ─────────────────────────────────► speakers
//
// The delay is what makes lip-sync work: the audio the renderer is drawing
// mouth shapes for must not reach the ear before those frames are on screen.
// Nothing is downloaded here — no ONNX models, no avatar bundle, no WebGPU.
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var SAMPLE_RATE = 16000;
var BLOCK_SAMPLES = 640; // 40 ms — the renderer's unit of work, 1 frame each
var BLOCKS_PER_STEP = 5; // 200 ms of audio rendered per request
// Encoder right-context: audio the model sees but does not render. The python
// generator sends 8 blocks (320 ms) and its comment is explicit that real
// future audio is what separates good lip-sync from zero padding.
var FUTURE_BLOCKS = 8;
// Per-block speech gate. livekit-plugins-talkbot uses 0.002 in the int16 RMS
// domain (0.002 * 32768); we are in float, so the constant is the same 0.002.
var SPEECH_RMS = 0.002;
var FPS = 25;
/** Linear resample to 16 kHz. Input is whatever rate the AudioContext runs at. */
function resampleTo16k(input, inRate) {
    var _a, _b;
    if (inRate === SAMPLE_RATE)
        return input;
    var ratio = inRate / SAMPLE_RATE;
    var out = new Float32Array(Math.floor(input.length / ratio));
    for (var i = 0; i < out.length; i++) {
        var pos = i * ratio;
        var i0 = Math.floor(pos);
        var frac = pos - i0;
        var a = (_a = input[i0]) !== null && _a !== void 0 ? _a : 0;
        var b = (_b = input[i0 + 1]) !== null && _b !== void 0 ? _b : a;
        out[i] = a + (b - a) * frac;
    }
    return out;
}
var RemoteAvatar = /** @class */ (function () {
    function RemoteAvatar(options) {
        this.sessionId = "w".concat(Math.random().toString(36).slice(2)).concat(Date.now().toString(36));
        this.ctx2d = null;
        // audio graph
        this.audioCtx = null;
        this.source = null;
        // ponytail: ScriptProcessor is deprecated but needs no worklet bundle; swap
        // for an AudioWorklet if main-thread jank ever shows up in profiles.
        this.tap = null;
        this.delayNode = null;
        this.gainNode = null;
        this.keepalive = null;
        // Utterance the renderer is mid-way through, or null between utterances.
        this.segmentId = null;
        this.segmentSeq = 0;
        this.speechSteps = 0;
        this.idleSteps = 0;
        // pcm accumulator (16 kHz mono float)
        this.pending = [];
        this.pendingLen = 0;
        this.frames = [];
        this.lastFrame = null;
        this.running = false;
        this.drawTimer = null;
        this.stepTimer = null;
        this.inFlight = false;
        this.started = false;
        this.opts = __assign({ viewMaxDim: 540, background: 'idle', sharpen: 1.1, 
            // prebuffer (400 ms) + lookahead (320 ms) + a render round trip.
            audioDelaySeconds: 0.95, prebufferFrames: 10, silenceRms: SPEECH_RMS }, options);
    }
    /** Warm the renderer session and learn the frame geometry. */
    RemoteAvatar.prototype.load = function () {
        return __awaiter(this, void 0, void 0, function () {
            var info, canvas;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, fetch("".concat(this.opts.gatewayUrl, "/session-info?").concat(this.params())).then(function (r) {
                            if (!r.ok)
                                throw new Error("gateway /session-info ".concat(r.status));
                            return r.json();
                        })];
                    case 1:
                        info = _a.sent();
                        canvas = this.opts.canvas;
                        canvas.width = info.width;
                        canvas.height = info.height;
                        this.ctx2d = canvas.getContext('2d');
                        return [2 /*return*/, info];
                }
            });
        });
    };
    /** Start rendering from the agent's audio track. */
    RemoteAvatar.prototype.start = function (agentAudio, audioCtx) {
        var _this = this;
        if (this.running)
            return;
        this.running = true;
        this.audioCtx = audioCtx;
        var stream = new MediaStream([agentAudio]);
        // Chrome delivers silence to a MediaStreamAudioSourceNode unless the same
        // stream is also attached to a media element. Muted, so it is the WebAudio
        // path below that is actually heard — this element only keeps the WebRTC
        // audio pipeline pumping. Without it every block reads as silence and the
        // avatar never leaves idle.
        var keepalive = new Audio();
        keepalive.srcObject = stream;
        keepalive.muted = true;
        keepalive.autoplay = true;
        void keepalive.play().catch(function () { return undefined; });
        this.keepalive = keepalive;
        this.source = audioCtx.createMediaStreamSource(stream);
        // Playback path: delayed, so the voice arrives with its frames.
        this.delayNode = audioCtx.createDelay(5);
        this.delayNode.delayTime.value = this.opts.audioDelaySeconds;
        this.gainNode = audioCtx.createGain();
        this.source.connect(this.delayNode);
        this.delayNode.connect(this.gainNode);
        this.gainNode.connect(audioCtx.destination);
        // Capture path: undelayed, feeds the renderer.
        this.tap = audioCtx.createScriptProcessor(4096, 1, 1);
        this.tap.onaudioprocess = function (e) {
            if (!_this.running)
                return;
            var pcm = resampleTo16k(e.inputBuffer.getChannelData(0), audioCtx.sampleRate);
            _this.pending.push(new Float32Array(pcm));
            _this.pendingLen += pcm.length;
        };
        this.source.connect(this.tap);
        // A ScriptProcessor only runs while connected to the graph; route it into a
        // muted gain so it ticks without being audible twice.
        var sink = audioCtx.createGain();
        sink.gain.value = 0;
        this.tap.connect(sink);
        sink.connect(audioCtx.destination);
        this.stepTimer = window.setInterval(function () { return void _this.step(); }, (BLOCKS_PER_STEP * 40));
        this.drawTimer = window.setInterval(function () { return _this.draw(); }, 1000 / FPS);
    };
    RemoteAvatar.prototype.setVolume = function (v) {
        if (this.gainNode)
            this.gainNode.gain.value = Math.max(0, Math.min(1, v));
    };
    RemoteAvatar.prototype.stop = function () {
        var _a, _b, _c, _d, _e;
        this.running = false;
        if (this.stepTimer !== null)
            window.clearInterval(this.stepTimer);
        if (this.drawTimer !== null)
            window.clearInterval(this.drawTimer);
        this.stepTimer = this.drawTimer = null;
        try {
            (_a = this.tap) === null || _a === void 0 ? void 0 : _a.disconnect();
            (_b = this.source) === null || _b === void 0 ? void 0 : _b.disconnect();
            (_c = this.delayNode) === null || _c === void 0 ? void 0 : _c.disconnect();
            (_d = this.gainNode) === null || _d === void 0 ? void 0 : _d.disconnect();
        }
        catch (_f) {
            /* graph already torn down */
        }
        if (this.keepalive) {
            this.keepalive.srcObject = null;
            this.keepalive = null;
        }
        this.frames.forEach(function (f) { var _a; return (_a = f.close) === null || _a === void 0 ? void 0 : _a.call(f); });
        this.frames = [];
        // Free the renderer's session state (encoder context, crossfade snapshot).
        (_e = navigator.sendBeacon) === null || _e === void 0 ? void 0 : _e.call(navigator, "".concat(this.opts.gatewayUrl, "/session/").concat(this.sessionId));
    };
    // ---------------------------------------------------------------- internals
    RemoteAvatar.prototype.params = function (extra) {
        if (extra === void 0) { extra = {}; }
        var o = this.opts;
        return new URLSearchParams(__assign({ session_id: this.sessionId, avatar_id: o.avatarId, background: o.background, sharpen: String(o.sharpen), view_max_dim: String(o.viewMaxDim) }, Object.fromEntries(Object.entries(extra).map(function (_a) {
            var k = _a[0], v = _a[1];
            return [k, String(v)];
        })))).toString();
    };
    /** Contiguous copy of the head of the accumulator, without consuming it. */
    RemoteAvatar.prototype.peek = function (samples) {
        var out = new Float32Array(samples);
        var filled = 0;
        for (var _i = 0, _a = this.pending; _i < _a.length; _i++) {
            var chunk = _a[_i];
            if (filled >= samples)
                break;
            var take = Math.min(chunk.length, samples - filled);
            out.set(chunk.subarray(0, take), filled);
            filled += take;
        }
        return out;
    };
    RemoteAvatar.prototype.consume = function (samples) {
        var left = samples;
        while (left > 0 && this.pending.length) {
            var head = this.pending[0];
            if (head.length <= left) {
                left -= head.length;
                this.pending.shift();
            }
            else {
                this.pending[0] = head.subarray(left);
                left = 0;
            }
        }
        this.pendingLen = Math.max(0, this.pendingLen - samples);
    };
    RemoteAvatar.toPcm16 = function (f) {
        var pcm = new Int16Array(f.length);
        for (var i = 0; i < f.length; i++) {
            var v = Math.max(-1, Math.min(1, f[i]));
            pcm[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
        }
        return pcm;
    };
    RemoteAvatar.rms = function (f, from, len) {
        var sum = 0;
        for (var i = from; i < from + len; i++)
            sum += f[i] * f[i];
        return Math.sqrt(sum / len);
    };
    RemoteAvatar.prototype.step = function () {
        return __awaiter(this, void 0, void 0, function () {
            var presentLen, futureLen, body, params, window_1, flags, b, hasSpeech, present, future, res, lens, buf, _a, off, _i, lens_1, len, blob, _b, _c, err_1;
            var _d, _e, _f, _g;
            return __generator(this, function (_h) {
                switch (_h.label) {
                    case 0:
                        // One request at a time: the renderer keeps per-session state, so steps
                        // must stay ordered. A late response just means the next tick sends more.
                        if (!this.running || this.inFlight)
                            return [2 /*return*/];
                        this.inFlight = true;
                        _h.label = 1;
                    case 1:
                        _h.trys.push([1, 8, 9, 10]);
                        presentLen = BLOCK_SAMPLES * BLOCKS_PER_STEP;
                        futureLen = BLOCK_SAMPLES * FUTURE_BLOCKS;
                        body = null;
                        params = {};
                        // A speech step needs its lookahead already buffered, so the pipeline
                        // runs one future-window behind live audio. Until that has built up
                        // (and whenever the agent is quiet) we render idle instead.
                        if (this.pendingLen >= presentLen + futureLen) {
                            window_1 = this.peek(presentLen + futureLen);
                            flags = new Uint8Array(BLOCKS_PER_STEP);
                            for (b = 0; b < BLOCKS_PER_STEP; b++) {
                                flags[b] =
                                    RemoteAvatar.rms(window_1, b * BLOCK_SAMPLES, BLOCK_SAMPLES) > this.opts.silenceRms
                                        ? 1
                                        : 0;
                            }
                            hasSpeech = flags.some(function (f) { return f === 1; });
                            // Mirrors the python generator: keep rendering speech while a segment
                            // is open, so the mouth closes properly instead of snapping to idle.
                            if (hasSpeech || this.segmentId) {
                                if (!this.segmentId)
                                    this.segmentId = "s".concat(++this.segmentSeq);
                                present = RemoteAvatar.toPcm16(window_1.subarray(0, presentLen));
                                future = RemoteAvatar.toPcm16(window_1.subarray(presentLen));
                                body = new FormData();
                                body.append('present', new Blob([present.buffer]), 'present.pcm');
                                body.append('future', new Blob([future.buffer]), 'future.pcm');
                                body.append('flags', new Blob([flags.buffer]), 'flags.bin');
                                params = { segment_id: this.segmentId };
                                if (!hasSpeech)
                                    this.segmentId = null; // utterance drained
                            }
                            this.consume(presentLen);
                        }
                        if (body)
                            this.speechSteps++;
                        else {
                            this.idleSteps++;
                            params = { idle_frames: BLOCKS_PER_STEP };
                        }
                        return [4 /*yield*/, fetch("".concat(this.opts.gatewayUrl, "/process-audio?").concat(this.params(params)), {
                                method: 'POST',
                                body: body !== null && body !== void 0 ? body : new FormData(),
                            })];
                    case 2:
                        res = _h.sent();
                        if (!res.ok)
                            throw new Error("gateway /process-audio ".concat(res.status));
                        lens = (res.headers.get('X-Frame-Lengths') || '')
                            .split(',')
                            .filter(Boolean)
                            .map(Number);
                        _a = Uint8Array.bind;
                        return [4 /*yield*/, res.arrayBuffer()];
                    case 3:
                        buf = new (_a.apply(Uint8Array, [void 0, _h.sent()]))();
                        off = 0;
                        _i = 0, lens_1 = lens;
                        _h.label = 4;
                    case 4:
                        if (!(_i < lens_1.length)) return [3 /*break*/, 7];
                        len = lens_1[_i];
                        blob = new Blob([buf.subarray(off, off + len)], { type: 'image/jpeg' });
                        off += len;
                        _c = (_b = this.frames).push;
                        return [4 /*yield*/, createImageBitmap(blob)];
                    case 5:
                        _c.apply(_b, [_h.sent()]);
                        _h.label = 6;
                    case 6:
                        _i++;
                        return [3 /*break*/, 4];
                    case 7:
                        // Bound the queue: a sustained backlog means we are behind, and stale
                        // frames are worse than a skip.
                        while (this.frames.length > FPS * 2)
                            (_e = (_d = this.frames.shift()) === null || _d === void 0 ? void 0 : _d.close) === null || _e === void 0 ? void 0 : _e.call(_d);
                        return [3 /*break*/, 10];
                    case 8:
                        err_1 = _h.sent();
                        (_g = (_f = this.opts).onError) === null || _g === void 0 ? void 0 : _g.call(_f, err_1 instanceof Error ? err_1 : new Error(String(err_1)));
                        return [3 /*break*/, 10];
                    case 9:
                        this.inFlight = false;
                        return [7 /*endfinally*/];
                    case 10: return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(RemoteAvatar.prototype, "stats", {
        /** speech vs idle step counts — if speech stays 0, no audio is reaching us. */
        get: function () {
            return { speechSteps: this.speechSteps, idleSteps: this.idleSteps, queued: this.frames.length };
        },
        enumerable: false,
        configurable: true
    });
    RemoteAvatar.prototype.draw = function () {
        var _a, _b, _c, _d;
        var ctx = this.ctx2d;
        if (!ctx)
            return;
        if (!this.started) {
            if (this.frames.length < this.opts.prebufferFrames)
                return;
            this.started = true;
            (_b = (_a = this.opts).onFirstFrame) === null || _b === void 0 ? void 0 : _b.call(_a);
        }
        var next = this.frames.shift();
        if (next) {
            (_d = (_c = this.lastFrame) === null || _c === void 0 ? void 0 : _c.close) === null || _d === void 0 ? void 0 : _d.call(_c);
            this.lastFrame = next;
        }
        // Underrun: hold the last frame rather than flashing an empty canvas.
        if (this.lastFrame)
            ctx.drawImage(this.lastFrame, 0, 0);
    };
    return RemoteAvatar;
}());
export { RemoteAvatar };
